import React from 'react';
import { observer } from 'mobx-react';
import { computed } from 'mobx';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import styled from '@emotion/styled';
import { Link } from 'react-router';
import { LearningPathStore, LearningPathWorksheetStore, LevelStore, UserGroupStore } from '@seedlang/stores';
import autobind from 'autobind-decorator';
import InPlaceText from 'components/form/in_place_text';
import InPlaceCheckbox from 'components/form/in_place_checkbox';
import InPlaceFlatSelect from 'components/form/in_place_flat_select';
import WorksheetSearch from 'components/worksheet_search';
import DeleteButton from 'components/button/delete_button';
import { AppUI } from '@seedlang/state';
import Copy from 'components/copy';
import Stack, { gaps } from 'components/layout/stack';
import Grid from 'components/layout/grid';
import ButtonPrimary from 'components/button/button_primary';
import WelcomeScreenCard from 'components/learning_path/welcome_screen_card';
import Spinner from 'components/spinner';
import { isPresent } from '@seedlang/utils';

const Wrapper = styled.div`
  table {
    margin-top: 20px;
  }
`;

const WelcomeScreenWrapper = styled.div`
  padding: ${gaps.md};
  display: flex;
  justify-content: center;
`;

@observer
class LearningPathEdit extends React.Component {

  constructor(props) {
    super(props);
  }

  componentDidMount() {
    this.getLearningPath();
    AppUI.worksheetStore.clearIndexData();
    LevelStore.getIndex();
  }

  componentWillUnmount() {
    LearningPathStore.clearShowData();
  }

  @autobind getLearningPath() {
    LearningPathStore.getShow({ids: {learningPathId: this.props.params.learningPathId}});
  }

  @autobind onAddWorksheet(params) {
    LearningPathWorksheetStore.create({data: {worksheetId: params.id, learningPathId: this.props.params.learningPathId}}, this.getLearningPath);
  }

  @autobind onRemoveWorksheet(id) {
    LearningPathWorksheetStore.destroy({ids: {learningPathWorksheetId: id}}, this.getLearningPath);
  }

  @computed get levelOptions() {
    if (!LevelStore.hasIndexData) { return; }
    return LevelStore.indexData.filter(level => isPresent(level.abbreviation) && level.abbreviation !== 'N').map(level => [level.id, level.abbreviation]);
  }

  @autobind handleViewLearningPath() {
    UserGroupStore.update({
      ids: { userGroupId: AppUI.user.currentUserGroup.id },
      data: { learningPathId: this.props.params.learningPathId },
    }, () => {
      window.open('/learn', '_blank');
    });
  }

  render() {
    return (
      <Wrapper>
        <div className='breadcrumbs' style={{marginBottom: 20}}>
          <Link to='creator.learning_paths.index'>
            Back to all Learning Paths
          </Link>
        </div>
        <Grid columns={[2, 1]} gap='sm'>
          <Stack gap='xs' alignItems='stretch'>
            <InPlaceText
              legend='Name'
              field='name'
              defaultValue={LearningPathStore.showDataField('name')}
              model='learning_paths'
              id={LearningPathStore.showDataField('id')}
              afterChange={this.getLearningPath}
            />
            <InPlaceText
              legend='Subtitle'
              field='subtitle'
              defaultValue={LearningPathStore.showDataField('subtitle')}
              model='learning_paths'
              id={LearningPathStore.showDataField('id')}
              afterChange={this.getLearningPath}
            />
            {LevelStore.hasIndexData && (
              <fieldset>
                <legend>Level</legend>
                <InPlaceFlatSelect
                  options={this.levelOptions}
                  model='learning_paths'
                  field='levelId'
                  value={LearningPathStore.showDataField('levelId')}
                  id={LearningPathStore.showDataField('id')}
                  afterChange={this.getLearningPath}
                />
              </fieldset>
            )}
            <InPlaceCheckbox
              legend='Published'
              field='published'
              value={LearningPathStore.showDataField('published')}
              model='learning_paths'
              id={LearningPathStore.showDataField('id')}
            >
              Published
            </InPlaceCheckbox>
          </Stack>
          <fieldset>
            <legend>Welcome Screen Preview</legend>
            <WelcomeScreenWrapper>
              {LearningPathStore.hasOutstandingRequests ? <Spinner /> : (
                <WelcomeScreenCard
                  title={LearningPathStore.showData.name}
                  subtitle={LearningPathStore.showData.subtitle}
                  imageUrl={LearningPathStore.showData.imageUrl}
                  levelAbbreviation={LearningPathStore.showData.levelAbbreviation}
                />
              )}
            </WelcomeScreenWrapper>
          </fieldset>
        </Grid>
        <fieldset>
          <legend>Posts</legend>
          <WorksheetSearch
            onSubmit={this.onAddWorksheet}
            placeholder='Search for Post to Add to this Learning Path'
          />
          {
            LearningPathStore.hasShowData &&
              <table className='table-wrapper admin-table'>
                <thead>
                  <tr>
                    <th>Position</th>
                    <th>Name</th>
                    <th>Post Published At</th>
                    <th>Content Published At</th>
                    <th width='40'></th>
                  </tr>
                </thead>
                <tbody>
                  {
                    LearningPathStore.showData.learningPathWorksheets.map(item => {
                      return (
                        <tr key={item.id}>
                          <td style={{width: '100px'}}>
                            <div className='text'>
                              <InPlaceText
                                field='position'
                                defaultValue={item.position}
                                model='learning_path_worksheets'
                                id={item.id}
                                afterChange={this.getLearningPath}
                              />
                            </div>
                          </td>
                          <td>
                            <div className='text' style={{display: 'flex', gap: '10px'}}>
                              {item.worksheet.name}
                              {
                                AppUI.user.developer &&
                                <Copy
                                  text={item.id}
                                />
                              }
                            </div>
                          </td>
                          <td>
                            <div>
                              {item.worksheet.publishedAt?.formattedDateWithYear}
                            </div>
                          </td>
                          <td>
                            <div>
                              {item.worksheet.contentPublishedAt?.formattedDateWithYear}
                            </div>
                          </td>
                          <td>
                            <DeleteButton
                              onConfirm={() => this.onRemoveWorksheet(item.id)}
                              message='Remove this post?'
                              className='fa-block right'
                            />
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
          }
        </fieldset>
        {LearningPathStore.hasShowData && (
          <ButtonPrimary
            style={{ marginTop: gaps.sm }}
            onClick={this.handleViewLearningPath}
            disabled={UserGroupStore.hasOutstandingRequests}
          >
            View this Learning Path
          </ButtonPrimary>
        )}
      </Wrapper>
    );
  }
}

export default ErrorBoundary(LearningPathEdit);